<mat-dialog-content class="content">
  <span class="sr-only" aria-live="polite" role="status">{{ resumeLoadingMessage }}</span>
  <!-- HEADER (shown on all steps except results which has its own) -->
  @if (step !== 'results') {
    <div class="dialog-header">
      <p class="header-title">Your Resume</p>
      <button type="button" class="close-btn" aria-label="Close dialog" (click)="closeDialog()">
        <img src="/assets/icons/close-icon.svg" alt="close" />
      </button>
    </div>
  }

  <!-- ===== STEP 1: INTRO ===== -->
  @if (step === 'intro') {
    <div class="dialog-body">
      <div class="body-text">
        <p class="dialog-title">Add Resume</p>
        <p class="dialog-description">
          Your resume unlocks personalized career matches, future job
          opportunities and a personalized plan built around your actual skills
          and experiences.
        </p>
        <p class="dialog-description">
          When you upload your resume, we analyze key details—like your education,
          experience, and skills—to tailor:
        </p>

        <ul class="benefits-list">
          <li>Career paths that fit your skills</li>
          <li>Clear next steps towards your goals</li>
          <li>Real jobs and internships you can apply to now</li>
        </ul>

        <p class="helper-text">
          Don't have a resume yet? Even a simple one works — you can always update it later.
        </p>

        <div class="actions-div">
          <button
            mat-flat-button
            type="button"
            class="start-btn"
            (click)="onClickStart()"
          >
            Start
          </button>
        </div>
      </div>

      <div class="body-illustration">
        <img
          src="/assets/images/results/add-resume-illustration.svg"
          alt="resume illustration"
          class="resume-illustration"
        />
      </div>
    </div>
  }

  <!-- ===== STEP 2: UPLOAD ===== -->
  @if (step === 'upload') {
    <div class="upload-step">
      <button type="button" class="back-btn" (click)="onClickBack()">
        <img src="/assets/icons/back-arrow.svg" alt="back" class="back-icon" />
        <span>BACK</span>
      </button>

      <div class="upload-content">
        <p class="upload-title">Upload your resume</p>

        <div class="upload-layout">
          <div class="upload-area-wrapper">
            @if (uploadError) {
              <div class="upload-error-banner" role="alert" aria-live="assertive">
                <img src="/assets/icons/warning-triangle.svg" alt="" class="error-icon" />
                <span>{{ uploadError }}</span>
              </div>
            }
            @if (isUploading) {
              <div class="drop-zone-loader" aria-hidden="true">
                <div class="drop-zone-loader-gradient"></div>
              </div>
            } @else {
              <div
                class="drop-zone"
                [class.dragging]="isDragging"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
                (click)="fileUpload.click()"
              >
                <input
                  type="file"
                  class="file-input"
                  [accept]="allowedFileTypes"
                  (change)="onFileSelected($event)"
                  #fileUpload
                />
                <div class="drop-zone-content">
                  <div class="plus-icon">
                    <img src="/assets/icons/plus-circle.svg" alt="plus" width="40" height="40" />
                  </div>
                  <p class="drop-text">
                    Drag and drop your file here
                  </p>
                  <p class="format-text">DOCX, PDF, and TXT formats, up to 2 MB, or</p>
                  <button type="button" class="browse-btn" (click)="fileUpload.click(); $event.stopPropagation()">Browse files</button>
                </div>
              </div>
            }
          </div>

          <div class="upload-info">
            <div class="info-header">
              <img src="/assets/icons/lightbulb.svg" alt="lightbulb" class="lightbulb-icon" />
              <p class="info-title">See your resume the way employers do</p>
            </div>
            <p class="info-description">
              Most companies use software called Applicant Tracking Systems (ATS) to screen resumes before a human ever reads them.
            </p>
            <p class="info-description">
              Compass uses the same technology, so you can see exactly what gets picked up, what gets missed, and how to make small tweaks that can make a big difference.
            </p>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ===== STEP 3: ANALYZING ===== -->
  @if (step === 'analyzing') {
    <div class="upload-step">
      <button type="button" class="back-btn" disabled>
        <img src="/assets/icons/back-arrow.svg" alt="back" class="back-icon" />
        <span>BACK</span>
      </button>

      <div class="upload-content">
        <p class="analyzing-title" aria-hidden="true">Analyzing your resume...</p>

        <div class="upload-layout">
          <div class="upload-area-wrapper">
            <div class="analyzing-preview" aria-hidden="true">
              <div class="analyzing-gradient"></div>
            </div>
          </div>

          <div class="upload-info">
            <div class="info-header">
              <img src="/assets/icons/lightbulb.svg" alt="lightbulb" class="lightbulb-icon" />
              <p class="info-title">See your resume the way employers do</p>
            </div>
            <p class="info-description">
              Most companies use software called Applicant Tracking Systems (ATS) to screen resumes before a human ever reads them.
            </p>
            <p class="info-description">
              Compass uses the same technology, so you can see exactly what gets picked up, what gets missed, and how to make small tweaks that can make a big difference.
            </p>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ===== STEP 4: RESULTS ===== -->
  @if (step === 'results') {
    <div class="results-step">
      <div class="results-header">
        <p class="header-title">Your Resume</p>
        <div class="results-header-actions">
          @if (mode === 'new') {
            <button
              mat-flat-button
              type="button"
              class="submit-results-btn"
              [disabled]="isSubmitting"
              (click)="onSubmitResults()"
            >
              {{ isSubmitting ? 'Submitting...' : 'Submit' }}
            </button>
          }
          <button type="button" class="close-btn" aria-label="Close dialog" (click)="closeDialog()">
            <img src="/assets/icons/close-icon.svg" alt="close" />
          </button>
        </div>
      </div>

      <div class="results-body" [class.results-body--docx]="isDocxFile">
        @if (isDocxFile) {
          <!-- File thumbnail card for DOCX -->
          <div class="file-thumbnail-card">
            <div class="file-thumbnail-inner">
              <span class="file-thumbnail-name">{{ filename }}</span>
            </div>
            <button type="button" class="file-download-btn" (click)="downloadResumeFile()">
              Download
              <img src="/assets/icons/right-arrow-short.svg" alt="" width="12" height="12" />
            </button>
          </div>
        } @else {
          <!-- PDF/TXT Preview -->
          <div class="pdf-preview-column">
            <div class="pdf-toolbar">
              <span class="pdf-toolbar-label">Preview: {{ filename }}</span>
            </div>
            <div class="pdf-viewer">
              @if (isLoadingExisting) {
                <div class="pdf-placeholder" aria-hidden="true">
                  <i class="fa-light fa-loader fa-spin loading-spinner-icon" aria-hidden="true"></i>
                </div>
              } @else if (pdfPreviewUrl) {
                <iframe
                  [src]="pdfPreviewUrl"
                  class="pdf-iframe"
                  title="Resume preview"
                ></iframe>
              } @else {
                <div class="pdf-placeholder">
                  <p>Preview not available</p>
                </div>
              }
            </div>
          </div>
        }

        <!-- Parsed Data -->
        <div class="parsed-data-column" [class.parsed-data-column--docx]="isDocxFile">
          @if (isLoadingExisting) {
            <div class="parsed-data-inner">
              <p class="parsed-title">Here's What We Found</p>
              <p class="parsed-subtitle">
                This is what an automated system detected from your resume—what came through clearly and what may have been missed.
              </p>

              <!-- Callout CTA -->
              <div class="callout-box">
                <div class="callout-content">
                  <img src="/assets/icons/lightbulb.svg" alt="lightbulb" class="callout-icon" />
                  <p class="callout-text">
                    <strong>Want better results?</strong> Formatting and structure can affect what gets detected. Try a few small adjustments and re-upload to see what changes.
                  </p>
                </div>
                <div class="reupload-wrapper">
                  <button
                    type="button"
                    class="reupload-btn"
                    (click)="toggleReuploadConfirm()"
                  >
                    Re-upload Resume
                  </button>
                </div>
              </div>

              <div class="section-divider"></div>
              <p class="section-title">WORK HISTORY</p>

              <div class="section-divider"></div>
              <p class="section-title">EDUCATION & CERTIFICATIONS</p>

              <div class="section-divider"></div>
              <p class="section-title">SKILLS DETECTED</p>
            </div>
          } @else {
          <div class="parsed-data-inner">
          <p class="parsed-title">Here's What We Found</p>
          <p class="parsed-subtitle">
            This is what an automated system detected from your resume—what came through clearly and what may have been missed.
          </p>

          <!-- Callout CTA -->
          <div class="callout-box">
            <div class="callout-content">
              <img src="/assets/icons/lightbulb.svg" alt="lightbulb" class="callout-icon" />
              <p class="callout-text">
                <strong>Want better results?</strong> Formatting and structure can affect what gets detected. Try a few small adjustments and re-upload to see what changes.
              </p>
            </div>
            <div class="reupload-wrapper">
              <button
                type="button"
                class="reupload-btn"
                (click)="toggleReuploadConfirm()"
              >
                Re-upload Resume
              </button>
              @if (showReuploadConfirm) {
                <div class="reupload-confirm-tooltip">
                  <span class="confirm-text">This will replace your current resume.</span>
                  <a class="confirm-continue" (click)="onReupload()">
                    Continue
                    <img src="/assets/icons/right-arrow-short.svg" alt="" class="confirm-arrow" />
                  </a>
                </div>
              }
            </div>
          </div>

          <!-- Contact Information (commented out for privacy review)
          <div class="section-divider"></div>
          <p class="section-title">CONTACT INFORMATION</p>
          @for (field of contactFields; track field.label) {
            <div class="parsed-row">
              @if (field.found) {
                <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
              } @else {
                <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              }
              <span class="parsed-label" [class.warning-label]="!field.found" (mouseenter)="!field.found && onInfoMissingHover()">
                {{ field.found ? field.label : field.warningLabel }}
                @if (!field.found) {
                  <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                }
              </span>
            </div>
          }
          -->

          <!-- Work History -->
          <div class="section-divider"></div>
          @if (resumeData?.workHistory?.length) {
            <p class="section-title">WORK HISTORY ({{ resumeData!.workHistory!.length }})</p>
            @for (job of resumeData!.workHistory!; track job.title; let i = $index) {
              <p class="subsection-title">ROLE {{ i + 1 }}</p>
              <div class="parsed-row">
                @if (job.title) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!job.title" (mouseenter)="!job.title && onInfoMissingHover()">
                  @if (job.title) {
                    <strong>Title:</strong> {{ job.title }}
                  } @else {
                    Did you include your role?
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
              <div class="parsed-row">
                @if (job.employer_name) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!job.employer_name" (mouseenter)="!job.employer_name && onInfoMissingHover()">
                  @if (job.employer_name) {
                    <strong>Employer:</strong> {{ job.employer_name }}
                  } @else {
                    Did you include an employer?
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
              <div class="parsed-row">
                @if (job.start_date || job.end_date) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!(job.start_date || job.end_date)" (mouseenter)="!(job.start_date || job.end_date) && onInfoMissingHover()">
                  @if (job.start_date || job.end_date) {
                    <strong>Date:</strong> {{ formatDateRange(job.start_date, job.end_date) }}
                  } @else {
                    Did you include dates?
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
              <!-- Description row hidden
              <div class="parsed-row">
                @if (job.summary) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!job.summary" (mouseenter)="!job.summary && onInfoMissingHover()">
                  {{ job.summary ? 'Description' : 'Did you include your job description?' }}
                  @if (!job.summary) {
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
              -->
            }
          } @else {
            <p class="section-title">WORK HISTORY</p>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you include your role?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you include an employer?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you include dates?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
          }

          <!-- Education & Certifications -->
          <div class="section-divider"></div>
          @if (resumeData?.education?.length) {
            <p class="section-title">EDUCATION & CERTIFICATIONS ({{ educationCertCount }})</p>
            @for (edu of resumeData!.education!; track edu.institution; let i = $index) {
              <p class="subsection-title">EDUCATION {{ i + 1 }}</p>
              <div class="parsed-row">
                @if (edu.level) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!edu.level" (mouseenter)="!edu.level && onInfoMissingHover()">
                  @if (edu.level) {
                    <strong>Credential:</strong> {{ edu.level }}
                  } @else {
                    Did you include a credential?
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
              <div class="parsed-row">
                @if (edu.institution) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!edu.institution" (mouseenter)="!edu.institution && onInfoMissingHover()">
                  @if (edu.institution) {
                    <strong>Institution:</strong> {{ edu.institution }}
                  } @else {
                    Did you share an institution?
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
              <div class="parsed-row">
                @if (edu.end_date) {
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                } @else {
                  <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
                }
                <span class="parsed-label" [class.warning-label]="!edu.end_date" (mouseenter)="!edu.end_date && onInfoMissingHover()">
                  @if (edu.end_date) {
                    <strong>Date:</strong> {{ formatEducationDate(edu.end_date) }}
                  } @else {
                    Did you include dates?
                    <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
                  }
                </span>
              </div>
            }
            @if (resumeData?.certifications?.length) {
              <p class="subsection-title">CERTIFICATIONS</p>
              @for (cert of resumeData!.certifications!; track cert) {
                <div class="parsed-row">
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                  <span class="parsed-label">{{ cert }}</span>
                </div>
              }
            }
          } @else {
            <p class="section-title">EDUCATION & CERTIFICATIONS</p>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you include a credential?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you share an institution?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you include dates?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
            @if (resumeData?.certifications?.length) {
              <p class="subsection-title">CERTIFICATIONS</p>
              @for (cert of resumeData!.certifications!; track cert) {
                <div class="parsed-row">
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                  <span class="parsed-label">{{ cert }}</span>
                </div>
              }
            }
          }

          <!-- Skills Detected -->
          <div class="section-divider"></div>
          @if (resumeData?.skills?.length) {
            <div class="section-title-row">
              <p class="section-title">SKILLS DETECTED ({{ resumeData!.skills!.length }})</p>
              <span class="info-icon-wrapper" (mouseenter)="onSkillsHover()">
                <img src="/assets/icons/info-icon.svg" alt="info" class="info-icon" width="16" height="16" />
                <span class="info-tooltip">Skills aren't always listed on a resume directly. We infer them from how you described your roles, education, experiences—so some may feel more obvious than others. The more detail your resume includes, the better we get at finding what you bring to the table.</span>
              </span>
            </div>
            <div class="skills-grid">
              @for (skill of resumeData!.skills!; track skill) {
                <div class="skill-item">
                  <img src="/assets/icons/teal-tick.svg" alt="" class="check-icon" width="18" height="18" />
                  <span class="parsed-label">{{ skill }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="section-title-row">
              <p class="section-title">SKILLS DETECTED</p>
              <span class="info-icon-wrapper" (mouseenter)="onSkillsHover()">
                <img src="/assets/icons/info-icon.svg" alt="info" class="info-icon" width="16" height="16" />
                <span class="info-tooltip">Skills aren't always listed on a resume directly. We infer them from how you described your roles, education, experiences—so some may feel more obvious than others. The more detail your resume includes, the better we get at finding what you bring to the table.</span>
              </span>
            </div>
            <div class="parsed-row">
              <img src="/assets/icons/yellow-warning.svg" alt="" class="warning-icon" width="18" height="18" />
              <span class="parsed-label warning-label" (mouseenter)="onInfoMissingHover()">
                Did you outline your job descriptions or describe your coursework?
                <span class="warning-tooltip">This may exist on your resume but wasn't detected. Adjusting your formatting may help.</span>
              </span>
            </div>
          }
       </div>
          }
       </div>
      </div>
    </div>
  }
</mat-dialog-content>
